
import { supabase } from "@/integrations/supabase/client";
import { generateVoiceMessage } from "./voiceService";
import { generateIntentSummary } from "./analysisService";

// For demo purposes, simulate completing calls with random results
export const simulateCampaignResults = async (leadIds: string[]): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!leadIds.length) {
      return { success: false, error: "No leads selected" };
    }

    // Example script to use for demo purposes
    const demoScript = "Hi there, I'm calling from Luxury Motors. We noticed you were interested in one of our vehicles recently. I wanted to check if you're still in the market for a new car? We have some special offers this month that might interest you.";

    // Generate a voice message using ElevenLabs (even in demo mode, to test the API)
    const { success: voiceSuccess, audioUrl, error: voiceError } = await generateVoiceMessage(demoScript);
    
    if (!voiceSuccess) {
      console.warn("Voice generation failed in demo mode:", voiceError);
      // Continue anyway for demo purposes
    }

    const callPromises = leadIds.map(async (leadId) => {
      // Randomly determine if lead is reactivated (60% chance)
      const randomStatus = Math.random() < 0.6 ? "reactivated" : 
                          (Math.random() < 0.5 ? "not-interested" : "completed");
      
      // Generate a sample transcript
      const transcript = randomStatus === "reactivated" 
        ? "Yes, I'm definitely still interested in that red SUV we talked about. I was just waiting for my bonus to come through. I'd like to come in this weekend if possible."
        : "I don't think I'm interested anymore. The prices were a bit out of my budget range.";
      
      // Use Claude to generate intent summary for the demo transcript
      const intentAnalysis = await generateIntentSummary(transcript);
      
      // Default values in case the Claude API fails
      let intentScore = randomStatus === "reactivated" ? 0.85 : 0.2;
      let intentSummary = randomStatus === "reactivated" 
        ? "Customer is interested in coming back to the dealership this weekend." 
        : "Customer is not interested at this time.";
      
      // Use Claude's analysis if available
      if (intentAnalysis.success) {
        intentScore = intentAnalysis.intentScore || intentScore;
        intentSummary = intentAnalysis.intentSummary || intentSummary;
      }
      
      // Update lead status
      const { error: leadError } = await supabase
        .from("leads")
        .update({ status: randomStatus })
        .eq("id", leadId);
      
      if (leadError) throw leadError;

      // Update call record
      const { error: callError } = await supabase
        .from("calls")
        .update({ 
          status: "completed",
          transcript: transcript,
          intent_summary: intentSummary,
          intent_score: intentScore,
          audio_url: audioUrl || "https://example.com/sample-audio.mp3"
        })
        .eq("lead_id", leadId);
      
      if (callError) throw callError;
    });

    await Promise.all(callPromises);

    return { success: true };
  } catch (error) {
    console.error("Error simulating campaign results:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};
